import { useConvex } from "convex/react";
import type {
  Layer as LeafletLayer,
  Map as LeafletMap,
  Marker as LeafletMarker,
  LeafletMouseEvent,
  TileLayer as LeafletTileLayer,
} from "leaflet";
import {
  ChevronDown,
  ChevronRight,
  Filter,
  Layers,
  ListFilter,
  Loader2,
  Lock,
  Navigation,
  Plus,
  Search,
  Unlock,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useIndexedDBCache } from "@/hooks/useIndexedDBCache";
import type { Hotspot, Panorama } from "@/store/editorStore";
import { useEditorStore } from "@/store/editorStore";
import { type Connection, generateNavigationHotspots } from "@/utils/autoHotspotGenerator";
import { api } from "../../convex/_generated/api";

interface MapLayer {
  id: string;
  name: string;
  url: string;
  attribution: string;
  maxZoom?: number;
  year?: number;
}

const POPULAR_LAYERS: MapLayer[] = [
  {
    id: "osm",
    name: "OpenStreetMap",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: "© OpenStreetMap contributors",
    maxZoom: 19,
    year: 2004,
  },
  {
    id: "carto-positron",
    name: "CartoDB Positron",
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    attribution: "© OpenStreetMap contributors © CARTO",
    maxZoom: 20,
    year: 2012,
  },
  {
    id: "carto-dark",
    name: "CartoDB Dark Matter",
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution: "© OpenStreetMap contributors © CARTO",
    maxZoom: 20,
    year: 2012,
  },
  {
    id: "carto-voyager",
    name: "CartoDB Voyager",
    url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
    attribution: "© OpenStreetMap contributors © CARTO",
    maxZoom: 20,
    year: 2015,
  },
  {
    id: "wikimedia",
    name: "Wikimedia",
    url: "https://maps.wikimedia.org/osm-intl/{z}/{x}/{y}.png",
    attribution: "© OpenStreetMap contributors © Wikimedia Foundation",
    maxZoom: 19,
    year: 2013,
  },
  {
    id: "esri-imagery",
    name: "Esri World Imagery",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      "Tiles © Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
    maxZoom: 19,
    year: 2009,
  },
];

const Maps = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    project,
    panoramas,
    setPanoramas,
    refreshPanoramas,
    hotspots,
    setHotspots,
    setSelectedPanorama,
    isPreviewMode,
  } = useEditorStore();
  const { toast } = useToast();
  const { user } = useAuth();
  const convex = useConvex();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<LeafletMap | null>(null);
  const markersRef = useRef<Map<string, LeafletMarker>>(new Map());
  const connectionsRef = useRef<Map<string, LeafletLayer>>(new Map());
  const connectionMetadataRef = useRef<Map<string, Connection>>(new Map()); // Store connection metadata for click handlers
  const isUpdatingConnectionsRef = useRef(false); // Flag to prevent extraction useEffect from overwriting manual updates
  const searchMarkerRef = useRef<LeafletMarker | null>(null); // Store search result marker
  const hasInitialViewSetRef = useRef(false); // Track if initial view has been set
  const tileLayerRef = useRef<LeafletTileLayer | null>(null); // Store current tile layer reference
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    lat: number;
    lng: number;
    x: number;
    y: number;
  } | null>(null);
  const [showPanoramaSubmenu, setShowPanoramaSubmenu] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [isLocked, setIsLocked] = useState(true); // Default to locked (panoramas cannot be moved)
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [markerContextMenuOpen, setMarkerContextMenuOpen] = useState(false);
  const [markerContextMenuPosition, setMarkerContextMenuPosition] = useState<{
    panoramaId: string;
    x: number;
    y: number;
  } | null>(null);
  const [currentLayer, setCurrentLayer] = useState<MapLayer>(POPULAR_LAYERS[0]);
  const [previewLayer, setPreviewLayer] = useState<MapLayer | null>(null);
  const [customLayerUrl, setCustomLayerUrl] = useState("");
  const [customLayerAttribution, setCustomLayerAttribution] = useState("");
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const previewTileLayerRef = useRef<LeafletTileLayer | null>(null); // Store preview tile layer reference
  const { getThumbnail, createBlobURL, isInitialized: isCacheInitialized } = useIndexedDBCache();
  const initialMapViewRef = useRef<{ view: [number, number]; zoom: number }>({
    view: [20, 0],
    zoom: 2,
  });
  const panoramasCountRef = useRef(panoramas.length);

  // Filter and View states
  const [currentView, setCurrentView] = useState<string>("all");
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(false);
  const [filters, setFilters] = useState<
    Array<{ property: string; operator: string; value: string }>
  >([]);
  const [filteredPanoramas, setFilteredPanoramas] = useState<Panorama[]>([]);
  const [visibleProperties, setVisibleProperties] = useState<Set<string>>(
    new Set(["name", "coordinates", "altitude", "created_at"]),
  );

  useEffect(() => {
    panoramasCountRef.current = panoramas.length;
  }, [panoramas.length]);

  useEffect(() => {
    let view: [number, number] = [20, 0];
    let zoom = 2;

    if (project?.data) {
      const projectData = project.data as {
        defaultMapView?: { lat?: number; lng?: number; zoom?: number };
      };
      if (projectData.defaultMapView) {
        const { lat, lng, zoom: projectZoom } = projectData.defaultMapView;
        if (typeof lat === "number" && typeof lng === "number") {
          view = [lat, lng];
        }
        if (typeof projectZoom === "number") {
          zoom = projectZoom;
        }
      }
    }

    initialMapViewRef.current = { view, zoom };
  }, [project?.data]);

  // Filter panoramas based on active filters
  useEffect(() => {
    if (filters.length === 0) {
      setFilteredPanoramas(panoramas);
      return;
    }

    const filtered = panoramas.filter((panorama) => {
      return filters.every((filter) => {
        const { property, operator, value } = filter;

        // Get property value from panorama
        let propertyValue: string | number | null = null;

        if (property === "name") {
          propertyValue = panorama.name;
        } else if (property === "coordinates") {
          const gpsLocation = panorama.gps_location as {
            lat?: number;
            lng?: number;
            latitude?: number;
            longitude?: number;
          } | null;
          if (gpsLocation) {
            const lat = gpsLocation.lat ?? gpsLocation.latitude;
            const lng = gpsLocation.lng ?? gpsLocation.longitude;
            propertyValue =
              lat != null && lng != null ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : null;
          }
        } else if (property === "altitude") {
          const gpsLocation = panorama.gps_location as { altitude?: number } | null;
          propertyValue = gpsLocation?.altitude ?? null;
        } else if (property === "created_at") {
          propertyValue = panorama.created_at;
        } else if (property === "file_size") {
          propertyValue = panorama.file_size;
        } else if (property === "width") {
          propertyValue = panorama.width;
        } else if (property === "height") {
          propertyValue = panorama.height;
        } else if (property === "order") {
          propertyValue = panorama.order;
        } else if (property === "file_status") {
          propertyValue = panorama.file_status;
        } else if (property === "processing_status") {
          propertyValue = panorama.processing_status;
        }

        // Apply operator
        if (propertyValue === null || propertyValue === undefined) {
          return operator === "is empty";
        }

        const valueStr = String(propertyValue).toLowerCase();
        const filterValueStr = value.toLowerCase();

        switch (operator) {
          case "is":
            return valueStr === filterValueStr;
          case "is not":
            return valueStr !== filterValueStr;
          case "contains":
            return valueStr.includes(filterValueStr);
          case "does not contain":
            return !valueStr.includes(filterValueStr);
          case "is empty":
            return valueStr === "" || propertyValue === null;
          case "is not empty":
            return valueStr !== "" && propertyValue !== null;
          default:
            return true;
        }
      });
    });

    setFilteredPanoramas(filtered);
  }, [panoramas, filters]);

  // Load hotspots from database
  const loadHotspots = useCallback(async () => {
    if (!projectId) return;
    try {
      const hotspotsData = (await convex.query(api.hotspots.listByProject, {
        projectId,
      })) as Hotspot[];
      setHotspots(hotspotsData || []);
    } catch (error) {
      console.error("Failed to load hotspots:", error);
    }
  }, [convex, projectId, setHotspots]);

  // Ensure panoramas and hotspots are loaded when Maps page is accessed
  useEffect(() => {
    if (projectId) {
      if (panoramas.length === 0) {
        refreshPanoramas(projectId).catch((error) => {
          console.error("Failed to load panoramas:", error);
        });
      }
      // Load hotspots
      if (hotspots.length === 0) {
        loadHotspots();
      }
    }
  }, [projectId, panoramas.length, hotspots.length, refreshPanoramas, loadHotspots]);

  // Handle deleting hotspots for a connection (only the specific direction clicked)
  const handleDeleteConnection = useCallback(
    async (connection: Connection) => {
      if (!projectId || !user) {
        console.warn("[Map] Cannot delete connection - missing projectId or user");
        return;
      }

      try {
        // Find hotspot that matches ONLY this specific direction (not bidirectional)
        const matchingHotspot = hotspots.find((hotspot) => {
          const content = hotspot.content as { linkedPanoramaId?: string } | undefined;
          const linkedPanoramaId = content?.linkedPanoramaId;

          // Only match the exact direction: fromPanoramaId -> toPanoramaId
          return (
            hotspot.panorama_id === connection.fromPanoramaId &&
            linkedPanoramaId === connection.toPanoramaId
          );
        });

        if (!matchingHotspot) {
          console.warn("[Map] No hotspot found for connection direction");
          toast({
            variant: "destructive",
            title: "No hotspot found",
            description: "No navigation hotspot found for this direction.",
          });
          return;
        }

        // Delete hotspot from database immediately (no confirmation)
        await convex.mutation(api.hotspots.remove, { id: matchingHotspot.id });

        // Update local store
        const updatedHotspots = hotspots.filter((h) => h.id !== matchingHotspot.id);
        setHotspots(updatedHotspots);

        // Reload hotspots to ensure consistency
        await loadHotspots();

        toast({
          title: "Hotspot removed",
          description: "Navigation hotspot deleted.",
        });

        console.log("[Map] Deleted hotspot for connection direction:", {
          connection: `${connection.fromPanoramaId} -> ${connection.toPanoramaId}`,
          hotspotId: matchingHotspot.id,
        });
      } catch (error) {
        console.error("[Map] Error in handleDeleteConnection:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to delete connection.",
        });
      }
    },
    [convex, hotspots, loadHotspots, projectId, setHotspots, toast, user],
  );

  // Extract connections from existing hotspots for visualization
  useEffect(() => {
    // Skip extraction if we're manually updating connections (e.g., during drag)
    if (isUpdatingConnectionsRef.current) {
      console.log("[Map] Skipping extraction - manual update in progress");
      return;
    }

    console.log("[Map] Extracting connections:", {
      hotspotsCount: hotspots.length,
      panoramasCount: panoramas.length,
    });

    if (hotspots.length === 0 || panoramas.length === 0) {
      console.log("[Map] No hotspots or panoramas, clearing connections");
      setConnections([]);
      return;
    }

    const existingConnections: Connection[] = [];
    const connectionSet = new Set<string>(); // Track added connections to avoid duplicates

    let hotspotsWithLinks = 0;
    hotspots.forEach((hotspot) => {
      const content = hotspot.content as { linkedPanoramaId?: string } | undefined;
      if (content?.linkedPanoramaId) {
        hotspotsWithLinks++;
        const fromPanorama = panoramas.find((p) => p.id === hotspot.panorama_id);
        const toPanorama = panoramas.find((p) => p.id === content.linkedPanoramaId);

        console.log("[Map] Found hotspot with link:", {
          hotspotId: hotspot.id,
          fromPanoramaId: hotspot.panorama_id,
          toPanoramaId: content.linkedPanoramaId,
          hasFromPanorama: !!fromPanorama,
          hasToPanorama: !!toPanorama,
          fromPanoramaName: fromPanorama?.name,
          toPanoramaName: toPanorama?.name,
        });

        if (fromPanorama && toPanorama) {
          const gpsFrom = fromPanorama.gps_location as {
            lat?: number;
            lng?: number;
            latitude?: number;
            longitude?: number;
          } | null;
          const gpsTo = toPanorama.gps_location as {
            lat?: number;
            lng?: number;
            latitude?: number;
            longitude?: number;
          } | null;

          console.log("[Map] GPS locations:", {
            fromPanorama: fromPanorama.name,
            toPanorama: toPanorama.name,
            gpsFrom,
            gpsTo,
          });

          if (gpsFrom && gpsTo) {
            const latFrom = gpsFrom.lat ?? gpsFrom.latitude;
            const lngFrom = gpsFrom.lng ?? gpsFrom.longitude;
            const latTo = gpsTo.lat ?? gpsTo.latitude;
            const lngTo = gpsTo.lng ?? gpsTo.longitude;

            if (latFrom == null || lngFrom == null || latTo == null || lngTo == null) {
              console.warn("[Map] Missing GPS coordinate values:", {
                fromPanorama: fromPanorama.name,
                toPanorama: toPanorama.name,
                gpsFrom,
                gpsTo,
              });
              return;
            }

            const connectionKey = `${fromPanorama.id}-${toPanorama.id}`;
            // Only add if not already added
            if (!connectionSet.has(connectionKey)) {
              connectionSet.add(connectionKey);
              existingConnections.push({
                fromPanoramaId: fromPanorama.id,
                toPanoramaId: toPanorama.id,
                fromLat: latFrom,
                fromLng: lngFrom,
                toLat: latTo,
                toLng: lngTo,
              });
              console.log("[Map] Added connection:", connectionKey);
            }

            // Check if reverse connection exists (bidirectional)
            const reverseKey = `${toPanorama.id}-${fromPanorama.id}`;
            const hasReverse = hotspots.some((h) => {
              const content = h.content as { linkedPanoramaId?: string } | undefined;
              return (
                h.panorama_id === toPanorama.id && content?.linkedPanoramaId === fromPanorama.id
              );
            });

            // Add reverse connection if it exists in hotspots
            if (hasReverse && !connectionSet.has(reverseKey)) {
              connectionSet.add(reverseKey);
              existingConnections.push({
                fromPanoramaId: toPanorama.id,
                toPanoramaId: fromPanorama.id,
                fromLat: latTo,
                fromLng: lngTo,
                toLat: latFrom,
                toLng: lngFrom,
              });
              console.log("[Map] Added reverse connection:", reverseKey);
            }
          } else {
            console.warn("[Map] Missing GPS location:", {
              fromPanorama: fromPanorama.name,
              toPanorama: toPanorama.name,
              hasGpsFrom: !!gpsFrom,
              hasGpsTo: !!gpsTo,
            });
          }
        } else {
          console.warn("[Map] Panorama not found:", {
            fromPanoramaId: hotspot.panorama_id,
            toPanoramaId: content.linkedPanoramaId,
            hasFromPanorama: !!fromPanorama,
            hasToPanorama: !!toPanorama,
          });
        }
      }
    });

    console.log("[Map] Connection extraction complete:", {
      hotspotsWithLinks,
      connectionsFound: existingConnections.length,
      connections: existingConnections,
    });

    setConnections(existingConnections);
  }, [hotspots, panoramas]);

  // Render connection arrows on the map
  const renderConnectionArrows = useCallback(
    async (connectionsToRender: Connection[]) => {
      console.log("[Map] renderConnectionArrows called:", {
        hasMap: !!mapInstanceRef.current,
        connectionsCount: connectionsToRender.length,
        connections: connectionsToRender,
      });

      if (!mapInstanceRef.current || connectionsToRender.length === 0) {
        console.warn("[Map] Skipping arrow render - missing map or no connections");
        return;
      }

      const L = await import("leaflet");
      const map = mapInstanceRef.current;
      if (!map) {
        console.warn("[Map] Map instance is null");
        return;
      }

      console.log("[Map] Clearing existing connections, count:", connectionsRef.current.size);
      // Clear existing connections
      connectionsRef.current.forEach((connection) => {
        map.removeLayer(connection);
      });
      connectionsRef.current.clear();
      connectionMetadataRef.current.clear(); // Clear metadata as well
      console.log("[Map] Cleared existing connections");

      // Identify bidirectional connections
      const bidirectionalPairs = new Map<string, { forward: Connection; reverse: Connection }>();
      const processedConnections = new Set<string>();

      for (const connection of connectionsToRender) {
        const key = `${connection.fromPanoramaId}-${connection.toPanoramaId}`;
        const reverseKey = `${connection.toPanoramaId}-${connection.fromPanoramaId}`;

        if (processedConnections.has(key) || processedConnections.has(reverseKey)) continue;

        const reverseConnection = connectionsToRender.find(
          (c) =>
            c.fromPanoramaId === connection.toPanoramaId &&
            c.toPanoramaId === connection.fromPanoramaId,
        );

        if (reverseConnection) {
          bidirectionalPairs.set(key, { forward: connection, reverse: reverseConnection });
          processedConnections.add(key);
          processedConnections.add(reverseKey);
        }
      }

      // Draw each connection
      console.log(
        "[Map] Drawing connections, available markers:",
        Array.from(markersRef.current.keys()),
      );
      for (const connection of connectionsToRender) {
        const fromMarker = markersRef.current.get(connection.fromPanoramaId);
        const toMarker = markersRef.current.get(connection.toPanoramaId);

        console.log("[Map] Processing connection:", {
          from: connection.fromPanoramaId,
          to: connection.toPanoramaId,
          hasFromMarker: !!fromMarker,
          hasToMarker: !!toMarker,
        });

        if (!fromMarker || !toMarker) {
          console.warn("[Map] Skipping connection - missing markers:", {
            from: connection.fromPanoramaId,
            to: connection.toPanoramaId,
            hasFromMarker: !!fromMarker,
            hasToMarker: !!toMarker,
          });
          continue;
        }

        const fromLatLng = fromMarker.getLatLng();
        const toLatLng = toMarker.getLatLng();

        // Check if this is part of a bidirectional pair
        const key = `${connection.fromPanoramaId}-${connection.toPanoramaId}`;
        const reverseKey = `${connection.toPanoramaId}-${connection.fromPanoramaId}`;
        const isBidirectional = bidirectionalPairs.has(key) || bidirectionalPairs.has(reverseKey);

        let polylinePoints: L.LatLng[] = [fromLatLng, toLatLng];

        // Apply offset for bidirectional connections
        if (isBidirectional) {
          const pair = bidirectionalPairs.get(key) || bidirectionalPairs.get(reverseKey);
          if (pair) {
            // Determine if this is the forward or reverse connection by comparing both IDs
            const isForward =
              pair.forward.fromPanoramaId === connection.fromPanoramaId &&
              pair.forward.toPanoramaId === connection.toPanoramaId;

            // Convert lat/lng points to container pixels for accurate offset calculation
            const fromPoint = map.latLngToContainerPoint(fromLatLng);
            const toPoint = map.latLngToContainerPoint(toLatLng);

            // Calculate perpendicular direction vector (normalized)
            const dx = toPoint.x - fromPoint.x;
            const dy = toPoint.y - fromPoint.y;
            const length = Math.sqrt(dx * dx + dy * dy);

            if (length > 0) {
              // Perpendicular vector (90 degrees rotated): swap and negate one component
              const perpX = -dy / length;
              const perpY = dx / length;

              // Forward connection: +10px, Reverse connection: 0px (stays at center)
              const offsetPixels = isForward ? 10 : 0;

              // Apply offset in perpendicular direction
              const offsetFromPoint = L.point(
                fromPoint.x + offsetPixels * perpX,
                fromPoint.y + offsetPixels * perpY,
              );
              const offsetToPoint = L.point(
                toPoint.x + offsetPixels * perpX,
                toPoint.y + offsetPixels * perpY,
              );

              // Convert back to lat/lng
              const offsetFrom = map.containerPointToLatLng(offsetFromPoint);
              const offsetTo = map.containerPointToLatLng(offsetToPoint);

              polylinePoints = [offsetFrom, offsetTo];

              console.log("[Map] Applied bidirectional offset:", {
                connection: `${connection.fromPanoramaId} -> ${connection.toPanoramaId}`,
                forwardConnection: `${pair.forward.fromPanoramaId} -> ${pair.forward.toPanoramaId}`,
                reverseConnection: `${pair.reverse.fromPanoramaId} -> ${pair.reverse.toPanoramaId}`,
                isForward,
                offsetPixels,
              });
            }
          }
        }

        // Create thin visual polyline
        const polyline = L.polyline(polylinePoints, {
          color: "#3B82F6",
          weight: 2, // Thin visual line
          opacity: 0.7,
          dashArray: "5, 5",
          interactive: false, // Disable interaction on visual line
        }).addTo(map);

        // Create invisible thicker polyline overlay for easy clicking
        const clickableOverlay = L.polyline(polylinePoints, {
          color: "transparent",
          weight: 12, // Thick transparent click area
          opacity: 0,
          interactive: true,
          className: "connection-click-area",
        }).addTo(map);

        // Make clickable overlay handle clicks
        clickableOverlay.on("click", async (event: LeafletMouseEvent) => {
          event.originalEvent.stopPropagation(); // Prevent map click
          await handleDeleteConnection(connection);
        });

        // Add hover effect to both visual line and overlay
        clickableOverlay.on("mouseover", () => {
          polyline.setStyle({
            weight: 3,
            opacity: 0.9,
            color: "#EF4444", // Red on hover
          });
        });

        clickableOverlay.on("mouseout", () => {
          polyline.setStyle({
            weight: 2,
            opacity: 0.7,
            color: "#3B82F6", // Back to blue
          });
        });

        // Calculate bearing for arrow rotation
        const dx = toLatLng.lng - fromLatLng.lng;
        const dy = toLatLng.lat - fromLatLng.lat;
        const bearing = (Math.atan2(dx, dy) * 180) / Math.PI;

        // Create multiple arrow markers along the line (like >>>>>>>>>>>>)
        const arrowMarkers: LeafletMarker[] = [];
        const arrowSpacing = 40; // Spacing between arrows in pixels

        // Get points along the polyline
        const linePoints = polyline.getLatLngs() as L.LatLng[];
        if (linePoints.length >= 2) {
          // Calculate total distance in pixels
          const startPoint = map.latLngToContainerPoint(linePoints[0]);
          const endPoint = map.latLngToContainerPoint(linePoints[linePoints.length - 1]);
          const totalDistance = Math.sqrt(
            (endPoint.x - startPoint.x) ** 2 + (endPoint.y - startPoint.y) ** 2,
          );

          // Create arrows at regular intervals
          const numArrowsToCreate = Math.max(3, Math.floor(totalDistance / arrowSpacing));

          for (let i = 1; i <= numArrowsToCreate; i++) {
            const t = i / (numArrowsToCreate + 1); // Position along line (0 to 1, excluding endpoints)

            // Interpolate position along the line
            const fromPoint = map.latLngToContainerPoint(linePoints[0]);
            const toPoint = map.latLngToContainerPoint(linePoints[linePoints.length - 1]);
            const arrowPoint = L.point(
              fromPoint.x + (toPoint.x - fromPoint.x) * t,
              fromPoint.y + (toPoint.y - fromPoint.y) * t,
            );
            const arrowLatLng = map.containerPointToLatLng(arrowPoint);

            // Create arrow marker with lower z-index so it stays below thumbnails
            const arrowMarker = L.marker(arrowLatLng, {
              icon: L.divIcon({
                html: `<div style="
                width: 0;
                height: 0;
                border-left: 4px solid transparent;
                border-right: 4px solid transparent;
                border-bottom: 8px solid #3B82F6;
                transform: rotate(${bearing}deg);
                transform-origin: center;
                cursor: pointer;
              "></div>`,
                className: "connection-arrow",
                iconSize: [8, 8],
                iconAnchor: [4, 8],
              }),
              zIndexOffset: -500, // Lower z-index to keep arrows below thumbnails
            }).addTo(map);

            // Make arrow marker clickable
            arrowMarker.on("click", async (event: LeafletMouseEvent) => {
              event.originalEvent.stopPropagation(); // Prevent map click
              await handleDeleteConnection(connection);
            });

            arrowMarkers.push(arrowMarker);
          }
        }

        // Store connection reference and metadata
        const connectionKey = `${connection.fromPanoramaId}-${connection.toPanoramaId}`;
        connectionsRef.current.set(connectionKey, polyline);
        connectionsRef.current.set(`${connectionKey}-overlay`, clickableOverlay); // Store clickable overlay
        // Store all arrow markers
        arrowMarkers.forEach((marker, index) => {
          connectionsRef.current.set(`${connectionKey}-arrow-${index}`, marker);
        });
        connectionMetadataRef.current.set(connectionKey, connection); // Store metadata for click handler
        console.log("[Map] Added connection with", arrowMarkers.length, "arrows:", connectionKey);
      }

      console.log("[Map] Finished rendering arrows, total:", connectionsRef.current.size);
    },
    [handleDeleteConnection],
  );

  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Dynamically import Leaflet only when component mounts
    const initMap = async () => {
      try {
        const L = await import("leaflet");
        await import("leaflet/dist/leaflet.css");

        // Initialize map with default view or world view
        if (!mapInstanceRef.current && mapContainerRef.current) {
          const { view: initialView, zoom: initialZoom } = initialMapViewRef.current;

          const map = L.map(mapContainerRef.current).setView(initialView, initialZoom);

          // Add initial tile layer (OpenStreetMap)
          const initialLayer = POPULAR_LAYERS[0];
          const tileLayer = L.tileLayer(initialLayer.url, {
            attribution: initialLayer.attribution,
            maxZoom: initialLayer.maxZoom || 19,
          }).addTo(map);
          tileLayerRef.current = tileLayer;

          // Handle right-click context menu
          map.on("contextmenu", (event: LeafletMouseEvent) => {
            event.originalEvent.preventDefault();
            event.originalEvent.stopPropagation();
            const { lat, lng } = event.latlng;
            const containerPoint = event.containerPoint;
            const containerRect = mapContainerRef.current?.getBoundingClientRect();
            console.log("Context menu triggered:", { lat, lng, containerPoint, containerRect });
            if (containerRect) {
              const x = containerRect.left + containerPoint.x;
              const y = containerRect.top + containerPoint.y;
              console.log("Setting context menu position:", { x, y });
              setContextMenuPosition({
                lat,
                lng,
                x,
                y,
              });
              setContextMenuOpen(true);
            }
          });

          // Handle map click to remove search marker
          map.on("click", (event: LeafletMouseEvent) => {
            // Don't remove marker if clicking on a marker or popup
            if (
              event.originalEvent.target.closest(".leaflet-marker-icon") ||
              event.originalEvent.target.closest(".leaflet-popup")
            ) {
              return;
            }

            // Remove search marker if it exists
            if (searchMarkerRef.current && mapInstanceRef.current) {
              mapInstanceRef.current.removeLayer(searchMarkerRef.current);
              searchMarkerRef.current = null;
            }
          });

          mapInstanceRef.current = map;

          // Handle map resize
          setTimeout(() => {
            map.invalidateSize();
          }, 100);

          // Trigger marker loading if panoramas are already loaded
          if (panoramasCountRef.current > 0) {
            setTimeout(() => {
              // Force re-run of marker loading effect
              const event = new CustomEvent("mapReady");
              window.dispatchEvent(event);
            }, 200);
          }
        }
      } catch (error) {
        console.error("Failed to load map:", error);
      }
    };

    initMap();

    // Cleanup function
    return () => {
      const map = mapInstanceRef.current;

      // Cleanup search marker
      if (searchMarkerRef.current && map) {
        map.removeLayer(searchMarkerRef.current);
        searchMarkerRef.current = null;
      }

      // Cleanup markers
      if (map) {
        markersRef.current.forEach((marker) => {
          if (marker) {
            map.removeLayer(marker);
          }
        });
        markersRef.current.clear();

        // Cleanup connections
        connectionsRef.current.forEach((connection) => {
          if (connection) {
            map.removeLayer(connection);
          }
        });
        connectionsRef.current.clear();
      }

      if (map) {
        map.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Load existing panorama markers
  useEffect(() => {
    if (!mapInstanceRef.current) {
      // Listen for map ready event
      const handleMapReady = () => {
        if (mapInstanceRef.current && panoramas.length > 0) {
          // Trigger marker loading by updating a dummy state or re-running effect
          setTimeout(() => {
            if (mapInstanceRef.current && panoramas.length > 0) {
              const loadMarkers = async () => {
                const L = await import("leaflet");
                const map = mapInstanceRef.current;
                if (!map) return;

                // Remove existing markers
                markersRef.current.forEach((marker) => {
                  map.removeLayer(marker);
                });
                markersRef.current.clear();

                const panoramaLocations: [number, number][] = [];

                // Add markers for panoramas with GPS locations
                const panoramasToRender = filters.length > 0 ? filteredPanoramas : panoramas;
                for (const panorama of panoramasToRender) {
                  const gpsLocation = panorama.gps_location as {
                    lat?: number;
                    lng?: number;
                    latitude?: number;
                    longitude?: number;
                  } | null;
                  if (!gpsLocation) {
                    console.log(`Panorama ${panorama.name} has no GPS location`);
                    continue;
                  }

                  const lat = gpsLocation.lat ?? gpsLocation.latitude;
                  const lng = gpsLocation.lng ?? gpsLocation.longitude;

                  if (lat === undefined || lng === undefined) {
                    console.log(
                      `Panorama ${panorama.name} has invalid GPS coordinates:`,
                      gpsLocation,
                    );
                    continue;
                  }

                  console.log(`Adding marker for panorama ${panorama.name} at ${lat}, ${lng}`);
                  panoramaLocations.push([lat, lng]);

                  try {
                    let thumbnailUrl: string | null = null;

                    if (isCacheInitialized) {
                      try {
                        let cachedThumbnail = await getThumbnail(panorama.id, true);
                        if (!cachedThumbnail) {
                          cachedThumbnail = await getThumbnail(panorama.id, false);
                        }
                        if (cachedThumbnail) {
                          thumbnailUrl = createBlobURL(
                            cachedThumbnail.data,
                            cachedThumbnail.mimeType,
                          );
                        }
                      } catch (_cacheError) {
                        // Cache error, fall through to network URL
                      }
                    }

                    if (!thumbnailUrl) {
                      thumbnailUrl = panorama.thumbnail_url_webp || panorama.thumbnail_url || null;
                    }

                    const iconSize = 40;
                    const iconHtml = thumbnailUrl
                      ? `<div style="width: ${iconSize}px; height: ${iconSize}px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); overflow: hidden; background: #1F2937; display: flex; align-items: center; justify-content: center; transition: transform 0.2s ease;"><img src="${thumbnailUrl}" style="width: 100%; height: 100%; object-fit: cover;" alt="${panorama.name}" /></div>`
                      : `<div style="width: ${iconSize}px; height: ${iconSize}px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); background: #3B82F6; display: flex; align-items: center; justify-content: center; color: white; font-size: 20px; transition: transform 0.2s ease;">📍</div>`;

                    const customIcon = L.divIcon({
                      html: iconHtml,
                      className: "custom-panorama-marker",
                      iconSize: [iconSize, iconSize],
                      iconAnchor: [iconSize / 2, iconSize / 2],
                    });

                    const marker = L.marker([lat, lng], {
                      icon: customIcon,
                      draggable: true, // Always create as draggable, then disable if locked
                    }).addTo(map);

                    // Disable dragging if locked
                    if (isLocked && marker.dragging) {
                      marker.dragging.disable();
                    }

                    // Add hover effect to scale thumbnail 4x
                    marker.on("mouseover", () => {
                      const iconElement = (marker as { _icon?: HTMLElement })._icon;
                      if (iconElement) {
                        const innerDiv = iconElement.querySelector("div");
                        if (innerDiv) {
                          innerDiv.style.transform = "scale(4)";
                        }
                        // Increase z-index on hover to ensure thumbnail appears above arrows
                        iconElement.style.zIndex = "800";
                      }
                    });

                    marker.on("mouseout", () => {
                      const iconElement = (marker as { _icon?: HTMLElement })._icon;
                      if (iconElement) {
                        const innerDiv = iconElement.querySelector("div");
                        if (innerDiv) {
                          innerDiv.style.transform = "scale(1)";
                        }
                        // Reset z-index when not hovering
                        iconElement.style.zIndex = "";
                      }
                    });

                    // Bind popup for hover tooltip (but prevent auto-open on click)
                    marker.bindPopup(
                      `<div style="text-align: center;"><strong>${panorama.name}</strong></div>`,
                      {
                        className: "panorama-popup",
                        closeOnClick: false,
                        autoClose: false,
                      },
                    );

                    // Unbind all click handlers first, then add our custom handler
                    // This prevents the popup from opening on click
                    marker.off("click");
                    marker.on("click", (event: LeafletMouseEvent) => {
                      // Prevent popup from opening
                      event.originalEvent.preventDefault();
                      event.originalEvent.stopPropagation();
                      marker.closePopup();

                      // Find the panorama
                      const clickedPanorama = panoramas.find((p) => p.id === panorama.id);
                      if (!clickedPanorama) return;

                      // Check if we're on the maps route
                      const isOnMapsRoute = location.pathname.includes("/maps");

                      // In preview/export mode: navigate to panorama in viewer
                      if (isPreviewMode) {
                        setSelectedPanorama(clickedPanorama);
                        // If on maps route, navigate to editor to show the panorama
                        if (isOnMapsRoute) {
                          navigate(`/${projectId}/editor`);
                        }
                      } else {
                        // In editor mode: select panorama and navigate to editor if on maps
                        if (isOnMapsRoute) {
                          setSelectedPanorama(clickedPanorama);
                          navigate(`/${projectId}/editor`);
                        } else {
                          // Already in editor, just select the panorama
                          setSelectedPanorama(clickedPanorama);
                        }
                      }
                    });

                    // Handle right-click on marker
                    marker.on("contextmenu", (event: LeafletMouseEvent) => {
                      event.originalEvent.preventDefault();
                      event.originalEvent.stopPropagation();
                      const containerPoint = event.containerPoint;
                      const containerRect = mapContainerRef.current?.getBoundingClientRect();
                      if (containerRect) {
                        const x = containerRect.left + containerPoint.x;
                        const y = containerRect.top + containerPoint.y;
                        setMarkerContextMenuPosition({
                          panoramaId: panorama.id,
                          x,
                          y,
                        });
                        setMarkerContextMenuOpen(true);
                      }
                    });

                    // Handle drag end to update GPS location and recalculate hotspots
                    marker.on("dragend", async (event: { target: LeafletMarker }) => {
                      const newLatLng = (event.target as LeafletMarker).getLatLng();
                      const newLat = newLatLng.lat;
                      const newLng = newLatLng.lng;

                      console.log("[Map] Drag end triggered:", {
                        panoramaId: panorama.id,
                        panoramaName: panorama.name,
                        oldPosition: { lat, lng },
                        newPosition: { lat: newLat, lng: newLng },
                        projectId,
                        hasUser: !!user,
                      });

                      try {
                        // Update panorama GPS location in database
                        console.log("[Map] Updating panorama GPS location in database...");
                        await convex.mutation(api.panoramas.update, {
                          id: panorama.id,
                          gps_location: { lat: newLat, lng: newLng },
                        });
                        console.log("[Map] Panorama GPS location updated successfully");

                        // Update local store
                        const updatedPanoramas = panoramas.map((p) =>
                          p.id === panorama.id
                            ? { ...p, gps_location: { lat: newLat, lng: newLng } }
                            : p,
                        );
                        setPanoramas(updatedPanoramas);
                        console.log("[Map] Local panorama store updated");

                        // Wait for panoramas state to update before proceeding
                        await new Promise((resolve) => setTimeout(resolve, 100));

                        // Recalculate hotspots automatically
                        if (projectId && user) {
                          console.log("[Map] Starting hotspot recalculation...");

                          // Delete all existing navigation hotspots to regenerate them
                          // This ensures hotspots are recalculated based on new positions
                          console.log("[Map] Fetching all hotspots...");
                          const allHotspots = (await convex.query(api.hotspots.listByProject, {
                            projectId,
                          })) as Hotspot[];
                          console.log("[Map] Fetched hotspots:", allHotspots?.length || 0);

                          // Filter to find navigation hotspots (those with linkedPanoramaId)
                          const navigationHotspotIds = (allHotspots || [])
                            .filter((hotspot) => {
                              const content = hotspot.content as
                                | { linkedPanoramaId?: string }
                                | undefined;
                              return !!content?.linkedPanoramaId;
                            })
                            .map((hotspot) => hotspot.id);

                          console.log(
                            "[Map] Found navigation hotspots to delete:",
                            navigationHotspotIds.length,
                          );

                          // Delete all navigation hotspots
                          if (navigationHotspotIds.length > 0) {
                            console.log("[Map] Deleting navigation hotspots...");
                            await Promise.all(
                              navigationHotspotIds.map((hotspotId) =>
                                convex.mutation(api.hotspots.remove, { id: hotspotId }),
                              ),
                            );
                            console.log(
                              "[Map] Successfully deleted",
                              navigationHotspotIds.length,
                              "navigation hotspots",
                            );
                          }

                          // Now regenerate hotspots with updated panorama positions
                          console.log("[Map] Generating new navigation hotspots...");
                          console.log(
                            "[Map] Panoramas with GPS:",
                            updatedPanoramas.filter((p) => {
                              const gps = p.gps_location as {
                                lat?: number;
                                latitude?: number;
                              } | null;
                              return gps && (gps.lat != null || gps.latitude != null);
                            }).length,
                          );

                          const result = await generateNavigationHotspots(
                            updatedPanoramas,
                            [], // Empty array since we just deleted all navigation hotspots
                            projectId,
                            user.id,
                          );

                          console.log("[Map] Hotspot generation result:", {
                            created: result.created,
                            skipped: result.skipped,
                            connectionsCount: result.connections.length,
                          });

                          // Refresh hotspots from database
                          console.log("[Map] Refreshing hotspots from database...");
                          await loadHotspots();

                          // Wait for extraction useEffect to run and extract connections from hotspots
                          // The extraction useEffect will automatically update connections state
                          console.log("[Map] Waiting for extraction useEffect to run...");
                          await new Promise((resolve) => setTimeout(resolve, 500));

                          // Clear existing arrows first
                          console.log("[Map] Clearing existing arrows...");
                          if (mapInstanceRef.current) {
                            const arrowCount = connectionsRef.current.size;
                            console.log("[Map] Found", arrowCount, "existing arrows to clear");
                            connectionsRef.current.forEach((connection) => {
                              mapInstanceRef.current.removeLayer(connection);
                            });
                            connectionsRef.current.clear();
                            console.log("[Map] Cleared all existing arrows");
                          }

                          // Wait a bit more for connections state to be updated by extraction useEffect
                          await new Promise((resolve) => setTimeout(resolve, 200));

                          // Get the current connections from state (which should be updated by extraction useEffect)
                          // We'll use a callback to get the latest connections
                          const _currentConnections = result.connections; // Fallback to result.connections

                          // Try to get connections from the extraction - wait for it
                          const checkConnections = () => {
                            // The extraction useEffect should have run by now
                            // We'll trigger a re-render by checking connections state
                            return new Promise<void>((resolve) => {
                              // Use a small delay to let React state updates propagate
                              setTimeout(() => {
                                resolve();
                              }, 100);
                            });
                          };

                          await checkConnections();

                          // Render arrows - the connections useEffect will handle rendering when connections change
                          // But we also manually trigger it here to ensure it happens
                          console.log(
                            "[Map] Triggering arrow render via connections state update...",
                          );
                          console.log(
                            "[Map] Expected connections count:",
                            result.connections.length,
                          );

                          // Set flag to prevent extraction useEffect from overwriting
                          isUpdatingConnectionsRef.current = true;

                          // Set connections to trigger the connections useEffect which will render arrows
                          setConnections(result.connections);
                          console.log("[Map] Set connections:", result.connections.length);

                          // Also manually render as backup
                          if (
                            result.connections.length > 0 &&
                            mapInstanceRef.current &&
                            markersRef.current.size > 0
                          ) {
                            console.log("[Map] Manually rendering arrows as backup...");
                            await renderConnectionArrows(result.connections);
                            console.log("[Map] Arrows rendered successfully");
                          } else {
                            console.warn(
                              "[Map] Skipping manual arrow render - missing conditions:",
                              {
                                hasConnections: result.connections.length > 0,
                                hasMap: !!mapInstanceRef.current,
                                hasMarkers: markersRef.current.size > 0,
                                markerCount: markersRef.current.size,
                              },
                            );
                          }

                          // Clear flag after a delay to allow extraction to run normally again
                          setTimeout(() => {
                            isUpdatingConnectionsRef.current = false;
                            console.log("[Map] Cleared update flag, extraction can run normally");
                          }, 1000);

                          toast({
                            title: "Panorama moved",
                            description: `${panorama.name} has been moved. Navigation hotspots recalculated.`,
                          });
                        } else {
                          console.warn(
                            "[Map] Skipping hotspot recalculation - missing projectId or user",
                          );
                        }
                      } catch (error) {
                        console.error("[Map] Failed to update panorama location:", error);
                        toast({
                          variant: "destructive",
                          title: "Error",
                          description: "Failed to update panorama location.",
                        });
                        // Revert marker position on error
                        marker.setLatLng([lat, lng]);
                      }
                    });

                    markersRef.current.set(panorama.id, marker);
                  } catch (error) {
                    console.error(`Failed to create marker for panorama ${panorama.id}:`, error);
                  }
                }

                // Only adjust view on initial load, not when panoramas are updated
                if (!hasInitialViewSetRef.current && panoramaLocations.length > 0) {
                  if (panoramaLocations.length === 1) {
                    map.setView(panoramaLocations[0], 15);
                  } else {
                    const bounds = L.latLngBounds(panoramaLocations);
                    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 18 });
                  }
                  hasInitialViewSetRef.current = true;
                }
              };
              loadMarkers();
            }
          }, 100);
        }
      };
      window.addEventListener("mapReady", handleMapReady);
      return () => window.removeEventListener("mapReady", handleMapReady);
    }

    // Wait a bit for panoramas to load if they're not loaded yet
    if (panoramas.length === 0) {
      // Try to refresh panoramas if we have a projectId
      if (projectId) {
        refreshPanoramas(projectId).catch((error) => {
          console.error("Failed to load panoramas:", error);
        });
      }
      return;
    }

    const loadMarkers = async () => {
      const L = await import("leaflet");
      const map = mapInstanceRef.current;
      if (!map) return;

      // Remove existing markers
      markersRef.current.forEach((marker) => {
        map.removeLayer(marker);
      });
      markersRef.current.clear();

      const panoramaLocations: [number, number][] = [];

      // Add markers for panoramas with GPS locations
      const panoramasToRender = filters.length > 0 ? filteredPanoramas : panoramas;
      for (const panorama of panoramasToRender) {
        const gpsLocation = panorama.gps_location as {
          lat?: number;
          lng?: number;
          latitude?: number;
          longitude?: number;
        } | null;
        if (!gpsLocation) {
          console.log(`Panorama ${panorama.name} has no GPS location`);
          continue;
        }

        // Handle both formats: {lat, lng} from Maps and {latitude, longitude} from EXIF
        const lat = gpsLocation.lat ?? gpsLocation.latitude;
        const lng = gpsLocation.lng ?? gpsLocation.longitude;

        if (lat === undefined || lng === undefined) {
          console.log(`Panorama ${panorama.name} has invalid GPS coordinates:`, gpsLocation);
          continue;
        }

        console.log(`Adding marker for panorama ${panorama.name} at ${lat}, ${lng}`);
        panoramaLocations.push([lat, lng]);

        try {
          // Get thumbnail
          let thumbnailUrl: string | null = null;

          // Try to get from cache first (if available)
          if (isCacheInitialized) {
            try {
              let cachedThumbnail = await getThumbnail(panorama.id, true); // WebP
              if (!cachedThumbnail) {
                cachedThumbnail = await getThumbnail(panorama.id, false); // JPEG
              }
              if (cachedThumbnail) {
                thumbnailUrl = createBlobURL(cachedThumbnail.data, cachedThumbnail.mimeType);
              }
            } catch (_cacheError) {
              // Cache error, fall through to network URL
            }
          }

          // Fallback to network URL
          if (!thumbnailUrl) {
            thumbnailUrl = panorama.thumbnail_url_webp || panorama.thumbnail_url || null;
          }

          // Create custom icon with thumbnail
          const iconSize = 40;
          const iconHtml = thumbnailUrl
            ? `<div style="
                width: ${iconSize}px;
                height: ${iconSize}px;
                border-radius: 50%;
                border: 3px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                overflow: hidden;
                background: #1F2937;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: transform 0.2s ease;
              ">
                <img 
                  src="${thumbnailUrl}" 
                  style="
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                  "
                  alt="${panorama.name}"
                />
              </div>`
            : `<div style="
                width: ${iconSize}px;
                height: ${iconSize}px;
                border-radius: 50%;
                border: 3px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                background: #3B82F6;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-size: 20px;
                transition: transform 0.2s ease;
              ">📍</div>`;

          const customIcon = L.divIcon({
            html: iconHtml,
            className: "custom-panorama-marker",
            iconSize: [iconSize, iconSize],
            iconAnchor: [iconSize / 2, iconSize / 2],
          });

          const marker = L.marker([lat, lng], {
            icon: customIcon,
            draggable: true, // Always create as draggable, then disable if locked
          }).addTo(map);

          // Disable dragging if locked
          if (isLocked && marker.dragging) {
            marker.dragging.disable();
          }

          // Add hover effect to scale thumbnail 4x
          marker.on("mouseover", () => {
            const iconElement = (marker as { _icon?: HTMLElement })._icon;
            if (iconElement) {
              const innerDiv = iconElement.querySelector("div");
              if (innerDiv) {
                innerDiv.style.transform = "scale(4)";
              }
              // Increase z-index on hover to ensure thumbnail appears above arrows
              iconElement.style.zIndex = "800";
            }
          });

          marker.on("mouseout", () => {
            const iconElement = (marker as { _icon?: HTMLElement })._icon;
            if (iconElement) {
              const innerDiv = iconElement.querySelector("div");
              if (innerDiv) {
                innerDiv.style.transform = "scale(1)";
              }
              // Reset z-index when not hovering
              iconElement.style.zIndex = "";
            }
          });

          // Bind popup for hover tooltip (but prevent auto-open on click)
          marker.bindPopup(
            `<div style="text-align: center;"><strong>${panorama.name}</strong></div>`,
            {
              className: "panorama-popup",
              closeOnClick: false,
              autoClose: false,
            },
          );

          // Unbind all click handlers first, then add our custom handler
          // This prevents the popup from opening on click
          marker.off("click");
          marker.on("click", (event: LeafletMouseEvent) => {
            // Prevent popup from opening
            event.originalEvent.preventDefault();
            event.originalEvent.stopPropagation();
            marker.closePopup();

            // Find the panorama
            const clickedPanorama = panoramas.find((p) => p.id === panorama.id);
            if (!clickedPanorama) return;

            // Check if we're on the maps route
            const isOnMapsRoute = location.pathname.includes("/maps");

            // In preview/export mode: navigate to panorama in viewer
            if (isPreviewMode) {
              setSelectedPanorama(clickedPanorama);
              // If on maps route, navigate to editor to show the panorama
              if (isOnMapsRoute) {
                navigate(`/${projectId}/editor`);
              }
            } else {
              // In editor mode: select panorama and navigate to editor if on maps
              if (isOnMapsRoute) {
                setSelectedPanorama(clickedPanorama);
                navigate(`/${projectId}/editor`);
              } else {
                // Already in editor, just select the panorama
                setSelectedPanorama(clickedPanorama);
              }
            }
          });

          // Handle right-click on marker
          marker.on("contextmenu", (event: LeafletMouseEvent) => {
            event.originalEvent.preventDefault();
            event.originalEvent.stopPropagation();
            const containerPoint = event.containerPoint;
            const containerRect = mapContainerRef.current?.getBoundingClientRect();
            if (containerRect) {
              const x = containerRect.left + containerPoint.x;
              const y = containerRect.top + containerPoint.y;
              setMarkerContextMenuPosition({
                panoramaId: panorama.id,
                x,
                y,
              });
              setMarkerContextMenuOpen(true);
            }
          });

          // Handle drag end to update GPS location and recalculate hotspots
          marker.on("dragend", async (event: { target: LeafletMarker }) => {
            const newLatLng = (event.target as LeafletMarker).getLatLng();
            const newLat = newLatLng.lat;
            const newLng = newLatLng.lng;

            console.log("[Map] Drag end triggered:", {
              panoramaId: panorama.id,
              panoramaName: panorama.name,
              oldPosition: { lat, lng },
              newPosition: { lat: newLat, lng: newLng },
              projectId,
              hasUser: !!user,
            });

            try {
              // Update panorama GPS location in database
              console.log("[Map] Updating panorama GPS location in database...");
              await convex.mutation(api.panoramas.update, {
                id: panorama.id,
                gps_location: { lat: newLat, lng: newLng },
              });
              console.log("[Map] Panorama GPS location updated successfully");

              // Update local store
              const updatedPanoramas = panoramas.map((p) =>
                p.id === panorama.id ? { ...p, gps_location: { lat: newLat, lng: newLng } } : p,
              );
              setPanoramas(updatedPanoramas);
              console.log("[Map] Local panorama store updated");

              // Wait for panoramas state to update before proceeding
              await new Promise((resolve) => setTimeout(resolve, 100));

              // Recalculate hotspots automatically
              if (projectId && user) {
                console.log("[Map] Starting hotspot recalculation...");

                // Delete all existing navigation hotspots to regenerate them
                // This ensures hotspots are recalculated based on new positions
                console.log("[Map] Fetching all hotspots...");
                const allHotspots = (await convex.query(api.hotspots.listByProject, {
                  projectId,
                })) as Hotspot[];
                console.log("[Map] Fetched hotspots:", allHotspots?.length || 0);

                // Filter to find navigation hotspots (those with linkedPanoramaId)
                const navigationHotspotIds = (allHotspots || [])
                  .filter((hotspot) => {
                    const content = hotspot.content as { linkedPanoramaId?: string } | undefined;
                    return !!content?.linkedPanoramaId;
                  })
                  .map((hotspot) => hotspot.id);

                console.log(
                  "[Map] Found navigation hotspots to delete:",
                  navigationHotspotIds.length,
                );

                // Delete all navigation hotspots
                if (navigationHotspotIds.length > 0) {
                  console.log("[Map] Deleting navigation hotspots...");
                  await Promise.all(
                    navigationHotspotIds.map((hotspotId) =>
                      convex.mutation(api.hotspots.remove, { id: hotspotId }),
                    ),
                  );
                  console.log(
                    "[Map] Successfully deleted",
                    navigationHotspotIds.length,
                    "navigation hotspots",
                  );
                }

                // Now regenerate hotspots with updated panorama positions
                console.log("[Map] Generating new navigation hotspots...");
                console.log(
                  "[Map] Panoramas with GPS:",
                  updatedPanoramas.filter((p) => {
                    const gps = p.gps_location as { lat?: number; latitude?: number } | null;
                    return gps && (gps.lat != null || gps.latitude != null);
                  }).length,
                );

                const result = await generateNavigationHotspots(
                  updatedPanoramas,
                  [], // Empty array since we just deleted all navigation hotspots
                  projectId,
                  user.id,
                );

                console.log("[Map] Hotspot generation result:", {
                  created: result.created,
                  skipped: result.skipped,
                  connectionsCount: result.connections.length,
                });

                // Refresh hotspots from database
                console.log("[Map] Refreshing hotspots from database...");
                await loadHotspots();

                // Wait for extraction useEffect to run and extract connections from hotspots
                // The extraction useEffect will automatically update connections state
                console.log("[Map] Waiting for extraction useEffect to run...");
                await new Promise((resolve) => setTimeout(resolve, 500));

                // Clear existing arrows first
                console.log("[Map] Clearing existing arrows...");
                if (mapInstanceRef.current) {
                  const arrowCount = connectionsRef.current.size;
                  console.log("[Map] Found", arrowCount, "existing arrows to clear");
                  connectionsRef.current.forEach((connection) => {
                    mapInstanceRef.current.removeLayer(connection);
                  });
                  connectionsRef.current.clear();
                  console.log("[Map] Cleared all existing arrows");
                }

                // Wait a bit more for connections state to be updated by extraction useEffect
                await new Promise((resolve) => setTimeout(resolve, 200));

                // Set flag to prevent extraction useEffect from overwriting
                isUpdatingConnectionsRef.current = true;

                // Set connections to trigger the connections useEffect which will render arrows
                setConnections(result.connections);
                console.log("[Map] Set connections:", result.connections.length);

                // Also manually render as backup
                if (
                  result.connections.length > 0 &&
                  mapInstanceRef.current &&
                  markersRef.current.size > 0
                ) {
                  console.log("[Map] Manually rendering arrows as backup...");
                  await renderConnectionArrows(result.connections);
                  console.log("[Map] Arrows rendered successfully");
                } else {
                  console.warn("[Map] Skipping manual arrow render - missing conditions:", {
                    hasConnections: result.connections.length > 0,
                    hasMap: !!mapInstanceRef.current,
                    hasMarkers: markersRef.current.size > 0,
                    markerCount: markersRef.current.size,
                  });
                }

                // Clear flag after a delay to allow extraction to run normally again
                setTimeout(() => {
                  isUpdatingConnectionsRef.current = false;
                  console.log("[Map] Cleared update flag, extraction can run normally");
                }, 1000);

                toast({
                  title: "Panorama moved",
                  description: `${panorama.name} has been moved. Navigation hotspots recalculated.`,
                });
              } else {
                console.warn("[Map] Skipping hotspot recalculation - missing projectId or user");
              }
            } catch (error) {
              console.error("[Map] Failed to update panorama location:", error);
              toast({
                variant: "destructive",
                title: "Error",
                description: "Failed to update panorama location.",
              });
              // Revert marker position on error
              marker.setLatLng([lat, lng]);
            }
          });

          markersRef.current.set(panorama.id, marker);
        } catch (error) {
          console.error(`Failed to create marker for panorama ${panorama.id}:`, error);
        }
      }

      // Center and zoom map to show all panoramas with GPS coordinates (only on initial load)
      if (!hasInitialViewSetRef.current && panoramaLocations.length > 0) {
        if (panoramaLocations.length === 1) {
          // Single panorama: center on it with reasonable zoom
          map.setView(panoramaLocations[0], 15);
        } else {
          // Multiple panoramas: fit bounds to show all
          const bounds = L.latLngBounds(panoramaLocations);
          map.fitBounds(bounds, { padding: [50, 50], maxZoom: 18 });
        }
        hasInitialViewSetRef.current = true;
      }

      // Render arrows after markers are loaded
      setTimeout(() => {
        if (connections.length > 0) {
          renderConnectionArrows(connections);
        }
      }, 300);
    };

    loadMarkers();
  }, [
    connections,
    convex,
    createBlobURL,
    filteredPanoramas,
    filters,
    getThumbnail,
    isCacheInitialized,
    isLocked,
    isPreviewMode,
    loadHotspots,
    location.pathname,
    navigate,
    panoramas,
    projectId,
    refreshPanoramas,
    renderConnectionArrows,
    setPanoramas,
    setSelectedPanorama,
    toast,
    user,
  ]);

  // Render connection arrows when connections change
  useEffect(() => {
    console.log("[Map] Connections useEffect triggered:", {
      hasMap: !!mapInstanceRef.current,
      connectionsCount: connections.length,
      markersCount: markersRef.current.size,
      markerIds: Array.from(markersRef.current.keys()),
    });

    if (!mapInstanceRef.current) {
      console.warn("[Map] No map instance, skipping arrow render");
      return;
    }

    // Wait for markers to be ready before rendering arrows
    if (markersRef.current.size === 0 && connections.length > 0) {
      console.log("[Map] Markers not ready yet, waiting...");
      // Markers not ready yet, wait a bit and retry
      const timer = setTimeout(() => {
        console.log("[Map] Retry after wait:", {
          markersCount: markersRef.current.size,
          connectionsCount: connections.length,
        });
        if (markersRef.current.size > 0 && connections.length > 0) {
          renderConnectionArrows(connections);
        }
      }, 500);
      return () => clearTimeout(timer);
    }

    // Render arrows if we have connections and markers
    if (connections.length > 0 && markersRef.current.size > 0) {
      console.log("[Map] Rendering arrows from useEffect");
      renderConnectionArrows(connections);
    } else if (connections.length === 0) {
      console.log("[Map] No connections, clearing arrows");
      // Clear arrows if no connections
      void import("leaflet").then(() => {
        if (mapInstanceRef.current) {
          connectionsRef.current.forEach((connection) => {
            mapInstanceRef.current.removeLayer(connection);
          });
          connectionsRef.current.clear();
        }
      });
    } else {
      console.warn("[Map] Conditions not met for arrow render:", {
        connectionsCount: connections.length,
        markersCount: markersRef.current.size,
      });
    }
  }, [connections, renderConnectionArrows]);

  // Update marker draggability when lock state changes
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    markersRef.current.forEach((marker) => {
      // Check if marker has dragging capability
      if (marker?.dragging) {
        if (isLocked) {
          marker.dragging.disable();
        } else {
          marker.dragging.enable();
        }
      }
    });
  }, [isLocked]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (mapInstanceRef.current) {
        setTimeout(() => {
          mapInstanceRef.current.invalidateSize();
        }, 100);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLinkPanorama = async (panoramaId: string) => {
    if (!contextMenuPosition || !projectId) return;

    try {
      const { lat, lng } = contextMenuPosition;

      // Update panorama GPS location
      await convex.mutation(api.panoramas.update, {
        id: panoramaId,
        gps_location: { lat, lng },
      });

      // Update store
      const updatedPanoramas = panoramas.map((p) =>
        p.id === panoramaId ? { ...p, gps_location: { lat, lng } } : p,
      );
      setPanoramas(updatedPanoramas);

      toast({
        title: "Panorama linked",
        description: "Panorama has been linked to the map location.",
      });

      setContextMenuOpen(false);
      setContextMenuPosition(null);
    } catch (error) {
      console.error("Failed to link panorama:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to link panorama to map location.",
      });
    }
  };

  const handleSetDefaultView = async () => {
    if (!mapInstanceRef.current || !projectId || !project) return;

    try {
      const map = mapInstanceRef.current;
      const center = map.getCenter();
      const zoom = map.getZoom();

      // Get current project data
      const currentData = (project.data as Record<string, unknown>) || {};
      const updatedData = {
        ...currentData,
        defaultMapView: {
          lat: center.lat,
          lng: center.lng,
          zoom: zoom,
        },
      };

      // Update project with default map view
      await convex.mutation(api.projects.update, { id: projectId, data: updatedData });

      // Update local project store
      const updatedProject = { ...project, data: updatedData };
      useEditorStore.setState({ project: updatedProject });

      toast({
        title: "Default view set",
        description: "This view will be used when loading the map.",
      });

      setContextMenuOpen(false);
      setContextMenuPosition(null);
    } catch (error) {
      console.error("Failed to set default view:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to set default map view.",
      });
    }
  };

  const handleAddManualHotspot = async () => {
    if (!mapInstanceRef.current || !contextMenuPosition) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Missing map or location information.",
      });
      return;
    }

    try {
      const { lat, lng } = contextMenuPosition;
      const map = mapInstanceRef.current;
      const L = await import("leaflet");

      // Create a simple marker at the clicked location
      const iconSize = 30;
      const iconHtml = `<div style="
        width: ${iconSize}px;
        height: ${iconSize}px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        background: #10B981;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 16px;
      ">📍</div>`;

      const customIcon = L.divIcon({
        html: iconHtml,
        className: "manual-marker",
        iconSize: [iconSize, iconSize],
        iconAnchor: [iconSize / 2, iconSize / 2],
      });

      const _marker = L.marker([lat, lng], { icon: customIcon }).addTo(map);

      // Store marker reference (you might want to use a separate ref for manual markers)
      // For now, we'll just add it to the map

      toast({
        title: "Marker added",
        description: `Marker added at ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      });

      setContextMenuOpen(false);
      setContextMenuPosition(null);
    } catch (error) {
      console.error("Failed to add marker:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add marker.",
      });
    }
  };

  const handleDeletePanoramaFromMap = async () => {
    if (!markerContextMenuPosition || !projectId) return;

    const panoramaId = markerContextMenuPosition.panoramaId;
    const panorama = panoramas.find((p) => p.id === panoramaId);

    if (!panorama) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Panorama not found.",
      });
      setMarkerContextMenuOpen(false);
      setMarkerContextMenuPosition(null);
      return;
    }

    try {
      // Delete GPS coordinates from database
      await convex.mutation(api.panoramas.update, {
        id: panoramaId,
        gps_location: null,
      });

      // Delete navigation hotspots that involve this panorama
      const allHotspots = (await convex.query(api.hotspots.listByProject, {
        projectId,
      })) as Hotspot[];

      if (allHotspots) {
        const hotspotsToDelete = allHotspots.filter((hotspot) => {
          const content = hotspot.content as { linkedPanoramaId?: string } | undefined;
          return hotspot.panorama_id === panoramaId || content?.linkedPanoramaId === panoramaId;
        });

        if (hotspotsToDelete.length > 0) {
          const hotspotIds = hotspotsToDelete.map((hotspot) => hotspot.id);
          try {
            await Promise.all(
              hotspotIds.map((hotspotId) =>
                convex.mutation(api.hotspots.remove, { id: hotspotId }),
              ),
            );
          } catch (deleteError) {
            console.warn("Failed to delete navigation hotspots:", deleteError);
          }

          // Update local hotspots store
          const updatedHotspots = hotspots.filter((hotspot) => {
            const content = hotspot.content as { linkedPanoramaId?: string } | undefined;
            return hotspot.panorama_id !== panoramaId && content?.linkedPanoramaId !== panoramaId;
          });
          setHotspots(updatedHotspots);
        }
      }

      // Update local store - remove GPS location
      const updatedPanoramas = panoramas.map((p) =>
        p.id === panoramaId ? { ...p, gps_location: null } : p,
      );
      setPanoramas(updatedPanoramas);

      // Remove marker from map
      const marker = markersRef.current.get(panoramaId);
      if (marker && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(marker);
        markersRef.current.delete(panoramaId);
      }

      // Remove any connections involving this panorama
      const updatedConnections = connections.filter(
        (c) => c.fromPanoramaId !== panoramaId && c.toPanoramaId !== panoramaId,
      );
      setConnections(updatedConnections);

      // Re-render connections without the deleted panorama
      if (updatedConnections.length > 0) {
        setTimeout(() => {
          renderConnectionArrows(updatedConnections);
        }, 100);
      } else {
        // Clear all connections if no connections remain
        if (mapInstanceRef.current) {
          connectionsRef.current.forEach((connection) => {
            mapInstanceRef.current.removeLayer(connection);
          });
          connectionsRef.current.clear();
        }
      }

      toast({
        title: "Panorama removed from map",
        description: `${panorama.name} has been removed from the map and GPS coordinates deleted.`,
      });

      setMarkerContextMenuOpen(false);
      setMarkerContextMenuPosition(null);
    } catch (error) {
      console.error("Failed to delete panorama from map:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete panorama from map.",
      });
    }
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (_event: MouseEvent) => {
      if (contextMenuOpen) {
        setContextMenuOpen(false);
        setShowPanoramaSubmenu(false);
        setContextMenuPosition(null);
      }
      if (markerContextMenuOpen) {
        setMarkerContextMenuOpen(false);
        setMarkerContextMenuPosition(null);
      }
    };

    if (contextMenuOpen || markerContextMenuOpen) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [contextMenuOpen, markerContextMenuOpen]);

  // Handle search for places
  const handleSearch = async () => {
    if (!searchQuery.trim() || !mapInstanceRef.current) return;

    setIsSearching(true);
    try {
      // Use Nominatim (OpenStreetMap) geocoding service
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`,
        {
          headers: {
            "User-Agent": "360Crafter/1.0", // Nominatim requires a User-Agent header
          },
        },
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const newLat = parseFloat(lat);
        const newLon = parseFloat(lon);

        const map = mapInstanceRef.current;
        const L = await import("leaflet");

        // Remove existing search marker if any
        if (searchMarkerRef.current) {
          map.removeLayer(searchMarkerRef.current);
          searchMarkerRef.current = null;
        }

        // Create a distinct marker for search results
        const iconSize = 40;
        const searchIconHtml = `<div style="
          width: ${iconSize}px;
          height: ${iconSize}px;
          border-radius: 50%;
          border: 3px solid #10B981;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          background: #10B981;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 20px;
        ">📍</div>`;

        const searchIcon = L.divIcon({
          html: searchIconHtml,
          className: "search-result-marker",
          iconSize: [iconSize, iconSize],
          iconAnchor: [iconSize / 2, iconSize / 2],
        });

        // Create and add marker
        const searchMarker = L.marker([newLat, newLon], {
          icon: searchIcon,
        }).addTo(map);

        // Add popup with location name
        searchMarker
          .bindPopup(
            `<div style="text-align: center;"><strong>${data[0].display_name}</strong></div>`,
            {
              className: "search-popup",
            },
          )
          .openPopup();

        // Store marker reference
        searchMarkerRef.current = searchMarker;

        // Navigate to location
        map.setView([newLat, newLon], 12);

        toast({
          title: "Location found",
          description: `Navigated to ${data[0].display_name}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Location not found",
          description: "Could not find the specified location. Please try a different search term.",
        });
      }
    } catch (error) {
      console.error("Geocoding error:", error);
      toast({
        variant: "destructive",
        title: "Search error",
        description: "Failed to search for location. Please try again.",
      });
    } finally {
      setIsSearching(false);
    }
  };

  // Preview map layer on hover (temporary)
  const previewMapLayer = async (layer: MapLayer) => {
    if (!mapInstanceRef.current || previewLayer?.id === layer.id || currentLayer.id === layer.id)
      return;

    const L = await import("leaflet");
    const map = mapInstanceRef.current;

    // Remove existing preview layer if any
    if (previewTileLayerRef.current) {
      map.removeLayer(previewTileLayerRef.current);
      previewTileLayerRef.current = null;
    }

    // Temporarily hide the current layer
    if (tileLayerRef.current) {
      tileLayerRef.current.setOpacity(0);
    }

    // Add preview tile layer
    const previewTileLayer = L.tileLayer(layer.url, {
      attribution: layer.attribution,
      maxZoom: layer.maxZoom || 19,
    }).addTo(map);

    previewTileLayerRef.current = previewTileLayer;
    setPreviewLayer(layer);
  };

  // Revert preview layer
  const revertPreviewLayer = () => {
    if (!mapInstanceRef.current || !previewTileLayerRef.current) return;

    const map = mapInstanceRef.current;

    // Remove preview layer
    map.removeLayer(previewTileLayerRef.current);
    previewTileLayerRef.current = null;
    setPreviewLayer(null);

    // Restore current layer opacity
    if (tileLayerRef.current) {
      tileLayerRef.current.setOpacity(1);
    }
  };

  // Switch map layer (permanent - on click)
  const switchMapLayer = async (layer: MapLayer) => {
    if (!mapInstanceRef.current) return;

    const L = await import("leaflet");
    const map = mapInstanceRef.current;

    // Remove preview layer if active
    if (previewTileLayerRef.current) {
      map.removeLayer(previewTileLayerRef.current);
      previewTileLayerRef.current = null;
      setPreviewLayer(null);
    }

    // Remove existing tile layer
    if (tileLayerRef.current) {
      map.removeLayer(tileLayerRef.current);
    }

    // Add new tile layer (full opacity for permanent layer)
    const newTileLayer = L.tileLayer(layer.url, {
      attribution: layer.attribution,
      maxZoom: layer.maxZoom || 19,
    }).addTo(map);

    tileLayerRef.current = newTileLayer;
    setCurrentLayer(layer);

    toast({
      title: "Map layer changed",
      description: `Switched to ${layer.name}`,
    });
  };

  // Handle custom layer
  const handleCustomLayer = async () => {
    if (!customLayerUrl.trim()) {
      toast({
        variant: "destructive",
        title: "Invalid URL",
        description: "Please enter a valid tile URL template.",
      });
      return;
    }

    const customLayer: MapLayer = {
      id: "custom",
      name: "Custom Layer",
      url: customLayerUrl,
      attribution: customLayerAttribution || "Custom map layer",
      maxZoom: 19,
    };

    await switchMapLayer(customLayer);
    setShowCustomDialog(false);
    setCustomLayerUrl("");
    setCustomLayerAttribution("");
  };

  // Check if navigation hotspots exist
  const hasNavigationHotspots = () => {
    return hotspots.some((hotspot) => {
      const content = hotspot.content as { linkedPanoramaId?: string } | undefined;
      return !!content?.linkedPanoramaId;
    });
  };

  // Remove all auto-generated navigation hotspots
  const handleRemoveNavigationHotspots = async () => {
    if (!projectId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Missing project information.",
      });
      return;
    }

    setIsGenerating(true);

    try {
      // Fetch all hotspots to find navigation hotspots
      const allHotspots = (await convex.query(api.hotspots.listByProject, {
        projectId,
      })) as Hotspot[];

      // Filter to find navigation hotspots (those with linkedPanoramaId)
      const navigationHotspotIds = (allHotspots || [])
        .filter((hotspot) => {
          const content = hotspot.content as { linkedPanoramaId?: string } | undefined;
          return !!content?.linkedPanoramaId;
        })
        .map((hotspot) => hotspot.id);

      if (navigationHotspotIds.length === 0) {
        toast({
          title: "No Navigation Hotspots",
          description: "No auto-generated navigation hotspots found.",
        });
        setIsGenerating(false);
        return;
      }

      // Delete all navigation hotspots
      await Promise.all(
        navigationHotspotIds.map((hotspotId) =>
          convex.mutation(api.hotspots.remove, { id: hotspotId }),
        ),
      );

      // Refresh hotspots from database
      await loadHotspots();

      // Clear connections
      setConnections([]);

      // Clear connection arrows from map
      connectionsRef.current.forEach((connection) => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.removeLayer(connection);
        }
      });
      connectionsRef.current.clear();
      connectionMetadataRef.current.clear();

      toast({
        title: "Navigation Hotspots Removed",
        description: `Removed ${navigationHotspotIds.length} navigation hotspot${navigationHotspotIds.length > 1 ? "s" : ""}.`,
      });
    } catch (error) {
      console.error("Failed to remove hotspots:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to remove navigation hotspots.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle auto-generation or removal of navigation hotspots
  const handleAutoGenerateHotspots = async () => {
    if (!projectId || !user || !project) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Missing project or user information.",
      });
      return;
    }

    // If navigation hotspots exist, remove them; otherwise generate them
    if (hasNavigationHotspots()) {
      await handleRemoveNavigationHotspots();
      return;
    }

    setIsGenerating(true);

    try {
      // Generate hotspots
      const result = await generateNavigationHotspots(panoramas, hotspots, projectId, user.id);

      // Refresh hotspots from database
      await loadHotspots();

      // The extraction useEffect will automatically extract connections from hotspots
      // and trigger arrow rendering via the connections useEffect.
      // Set connections immediately so arrows can be rendered right away
      setConnections(result.connections);

      // Wait a bit for hotspots state to update and ensure markers are ready
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Render arrows - the useEffect will also handle this, but this ensures immediate visibility
      if (result.connections.length > 0 && mapInstanceRef.current && markersRef.current.size > 0) {
        await renderConnectionArrows(result.connections);
      }

      toast({
        title: "Navigation Hotspots Generated",
        description: `Created ${result.created} navigation hotspots. ${result.skipped > 0 ? `${result.skipped} panoramas skipped (already have hotspots).` : ""}`,
      });
    } catch (error) {
      console.error("Failed to generate hotspots:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to generate navigation hotspots.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Available panorama properties
  const availableProperties = [
    { id: "name", label: "Name", type: "text" },
    { id: "coordinates", label: "Coordinates", type: "text" },
    { id: "altitude", label: "Altitude", type: "number" },
    { id: "created_at", label: "Created At", type: "date" },
    { id: "file_size", label: "File Size", type: "number" },
    { id: "width", label: "Width", type: "number" },
    { id: "height", label: "Height", type: "number" },
    { id: "order", label: "Order", type: "number" },
    { id: "file_status", label: "File Status", type: "text" },
    { id: "processing_status", label: "Processing Status", type: "text" },
  ];

  const addFilter = () => {
    setFilters([...filters, { property: "name", operator: "is", value: "" }]);
  };

  const removeFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const updateFilter = (
    index: number,
    field: "property" | "operator" | "value",
    newValue: string,
  ) => {
    const updated = [...filters];
    updated[index] = { ...updated[index], [field]: newValue };
    setFilters(updated);
  };

  const toggleProperty = (propertyId: string) => {
    const newVisible = new Set(visibleProperties);
    if (newVisible.has(propertyId)) {
      newVisible.delete(propertyId);
    } else {
      newVisible.add(propertyId);
    }
    setVisibleProperties(newVisible);
  };

  return (
    <div className="h-full w-full bg-gray-800 relative">
      {/* Header bar with Views, Filter, and Properties */}
      <div className="absolute top-0 left-0 right-0 h-12 bg-gray-800 border-b border-gray-700 z-[1001] flex items-center justify-between px-4">
        {/* Left side - Views */}
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-gray-400" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="text-white hover:bg-gray-700 h-8 px-2">
                {currentView === "all" ? "All views" : currentView}
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-gray-800 border-gray-700 text-white">
              <DropdownMenuItem
                onClick={() => setCurrentView("all")}
                className="text-white focus:bg-gray-700"
              >
                All views
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setCurrentView("this")}
                className="text-white focus:bg-gray-700"
              >
                This view
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <span className="text-gray-400 text-sm ml-2">{filteredPanoramas.length} results</span>
        </div>

        {/* Right side - Filter and Properties */}
        <div className="flex items-center gap-2">
          <Button
            variant={showFilterPanel ? "default" : "ghost"}
            onClick={() => {
              setShowFilterPanel(!showFilterPanel);
              if (showFilterPanel) setShowPropertiesPanel(false);
            }}
            className={`h-8 px-3 ${showFilterPanel ? "bg-blue-600 hover:bg-blue-700 text-white" : "text-white hover:bg-gray-700"}`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button
            variant={showPropertiesPanel ? "default" : "ghost"}
            onClick={() => {
              setShowPropertiesPanel(!showPropertiesPanel);
              if (showPropertiesPanel) setShowFilterPanel(false);
            }}
            className={`h-8 px-3 ${showPropertiesPanel ? "bg-blue-600 hover:bg-blue-700 text-white" : "text-white hover:bg-gray-700"}`}
          >
            <ListFilter className="h-4 w-4 mr-2" />
            Properties
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilterPanel && (
        <div className="absolute top-12 right-4 w-96 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-[1002] max-h-[calc(100vh-80px)] overflow-y-auto">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-sm flex items-center justify-between">
                <span>Filter</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilterPanel(false)}
                  className="h-6 w-6 p-0 text-gray-400 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-xs text-gray-400 mb-2">All the following are true</div>
              {filters.length === 0 ? (
                <div className="text-gray-400 text-sm py-4 text-center">No filters applied</div>
              ) : (
                filters.map((filter, index) => (
                  <div
                    key={`${filter.property}-${filter.operator}-${filter.value}-${index}`}
                    className="flex gap-2 items-start"
                  >
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <Select
                        value={filter.property}
                        onValueChange={(value) => updateFilter(index, "property", value)}
                      >
                        <SelectTrigger className="bg-gray-700 border-gray-600 text-white text-xs h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700 text-white">
                          {availableProperties.map((prop) => (
                            <SelectItem
                              key={prop.id}
                              value={prop.id}
                              className="text-white focus:bg-gray-700"
                            >
                              {prop.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={filter.operator}
                        onValueChange={(value) => updateFilter(index, "operator", value)}
                      >
                        <SelectTrigger className="bg-gray-700 border-gray-600 text-white text-xs h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700 text-white">
                          <SelectItem value="is" className="text-white focus:bg-gray-700">
                            is
                          </SelectItem>
                          <SelectItem value="is not" className="text-white focus:bg-gray-700">
                            is not
                          </SelectItem>
                          <SelectItem value="contains" className="text-white focus:bg-gray-700">
                            contains
                          </SelectItem>
                          <SelectItem
                            value="does not contain"
                            className="text-white focus:bg-gray-700"
                          >
                            does not contain
                          </SelectItem>
                          <SelectItem value="is empty" className="text-white focus:bg-gray-700">
                            is empty
                          </SelectItem>
                          <SelectItem value="is not empty" className="text-white focus:bg-gray-700">
                            is not empty
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        value={filter.value}
                        onChange={(e) => updateFilter(index, "value", e.target.value)}
                        placeholder="Value"
                        className="bg-gray-700 border-gray-600 text-white text-xs h-8"
                        disabled={
                          filter.operator === "is empty" || filter.operator === "is not empty"
                        }
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFilter(index)}
                      className="h-8 w-8 p-0 text-gray-400 hover:text-red-400"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
              <Button
                onClick={addFilter}
                variant="outline"
                size="sm"
                className="w-full bg-gray-700 border-gray-600 text-white hover:bg-gray-600 text-xs"
              >
                + Add filter
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Properties Panel */}
      {showPropertiesPanel && (
        <div className="absolute top-12 right-4 w-80 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-[1002] max-h-[calc(100vh-80px)] overflow-y-auto">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-sm flex items-center justify-between">
                <span>Properties</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPropertiesPanel(false)}
                  className="h-6 w-6 p-0 text-gray-400 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="text-xs text-gray-400 mb-2">Select properties to display</div>
              {availableProperties.map((prop) => (
                <div key={prop.id} className="flex items-center space-x-2 py-1">
                  <Checkbox
                    id={prop.id}
                    checked={visibleProperties.has(prop.id)}
                    onCheckedChange={() => toggleProperty(prop.id)}
                    className="border-gray-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                  <Label htmlFor={prop.id} className="text-sm text-gray-300 cursor-pointer flex-1">
                    {prop.label}
                  </Label>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search bar */}
      <div className="absolute top-16 left-[60px] z-[1000] flex gap-2 w-[min(520px,calc(100%-88px))]">
        <div className="flex-1 flex gap-2 bg-gray-800 border border-gray-700 rounded-md p-1">
          <Input
            placeholder="Search for a place, address, city, or country..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
            className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 flex-1"
            disabled={isSearching}
          />
          <Button
            onClick={handleSearch}
            disabled={isSearching || !searchQuery.trim()}
            variant="outline"
            size="sm"
            className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600 px-3"
          >
            {isSearching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Auto-generate button and lock toggle */}
      <div className="absolute top-16 right-4 z-[1000] flex flex-wrap justify-end items-center gap-2 max-w-[calc(100%-88px)]">
        <DropdownMenu
          onOpenChange={(open) => {
            // Revert preview when dropdown closes
            if (!open) {
              revertPreviewLayer();
            }
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="h-9 bg-gray-800 border-gray-700 text-white hover:bg-gray-700 shrink-0"
              title="Select map layer"
            >
              <Layers className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-56 bg-gray-800 border-gray-700 text-white"
            align="end"
            onMouseLeave={() => {
              // Revert preview when mouse leaves the dropdown
              revertPreviewLayer();
            }}
          >
            <DropdownMenuLabel>Map Layers</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-gray-700" />
            <DropdownMenuRadioGroup value={currentLayer.id}>
              {POPULAR_LAYERS.map((layer) => (
                <DropdownMenuRadioItem
                  key={layer.id}
                  value={layer.id}
                  onMouseEnter={() => previewMapLayer(layer)}
                  onMouseLeave={() => {
                    // Only revert if this is the preview layer and not the current layer
                    if (previewLayer?.id === layer.id && currentLayer.id !== layer.id) {
                      revertPreviewLayer();
                    }
                  }}
                  onClick={() => switchMapLayer(layer)}
                  className="text-white focus:bg-gray-700"
                >
                  {layer.name}
                  {layer.year ? ` (${layer.year})` : ""}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator className="bg-gray-700" />
            <Dialog open={showCustomDialog} onOpenChange={setShowCustomDialog}>
              <DialogTrigger asChild>
                <DropdownMenuItem
                  className="text-white focus:bg-gray-700 cursor-pointer"
                  onSelect={(e) => {
                    e.preventDefault();
                    setShowCustomDialog(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Custom URL...
                </DropdownMenuItem>
              </DialogTrigger>
              <DialogContent className="bg-gray-800 border-gray-700 text-white">
                <DialogHeader>
                  <DialogTitle>Add Custom Map Layer</DialogTitle>
                  <DialogDescription className="text-gray-400">
                    Enter a tile URL template. Use {"{s}"} for subdomains, {"{z}"} for zoom, {"{x}"}{" "}
                    and {"{y}"} for coordinates.
                    <br />
                    Example: https://{"{s}"}.tile.openstreetmap.org/{"{z}"}/{"{x}"}/{"{y}"}.png
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <label
                      className="text-sm font-medium text-gray-300 mb-2 block"
                      htmlFor="custom-layer-url"
                    >
                      Tile URL Template
                    </label>
                    <Input
                      id="custom-layer-url"
                      placeholder="https://{s}.example.com/{z}/{x}/{y}.png"
                      value={customLayerUrl}
                      onChange={(e) => setCustomLayerUrl(e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <label
                      className="text-sm font-medium text-gray-300 mb-2 block"
                      htmlFor="custom-layer-attribution"
                    >
                      Attribution (optional)
                    </label>
                    <Input
                      id="custom-layer-attribution"
                      placeholder="© Your Map Provider"
                      value={customLayerAttribution}
                      onChange={(e) => setCustomLayerAttribution(e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowCustomDialog(false)}
                    className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCustomLayer}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Load Layer
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          onClick={() => setIsLocked(!isLocked)}
          variant="outline"
          className="h-9 bg-gray-800 border-gray-700 text-white hover:bg-gray-700 shrink-0"
          title={isLocked ? "Unlock to move panoramas" : "Lock panoramas"}
        >
          {isLocked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
        </Button>
        <Button
          onClick={handleAutoGenerateHotspots}
          disabled={isGenerating || (!hasNavigationHotspots() && panoramas.length < 2)}
          variant="outline"
          className="h-9 bg-gray-800 border-gray-700 text-white hover:bg-gray-700 shrink-0"
          title={
            hasNavigationHotspots()
              ? "Remove auto-generated navigation hotspots"
              : "Auto-generate navigation hotspots"
          }
        >
          <Navigation className="h-4 w-4" />
          <span className="hidden xl:inline ml-2">
            {isGenerating
              ? hasNavigationHotspots()
                ? "Removing..."
                : "Generating..."
              : hasNavigationHotspots()
                ? "Remove Auto-Generated Navigation Hotspots"
                : "Auto-Generate Navigation Hotspots"}
          </span>
        </Button>
      </div>

      <div className="h-full w-full" ref={mapContainerRef} style={{ minHeight: "400px" }} />
      {contextMenuOpen && contextMenuPosition && (
        <div
          className="fixed bg-gray-800 border border-gray-700 rounded-md shadow-lg text-white min-w-[200px]"
          style={{
            left: `${contextMenuPosition.x}px`,
            top: `${contextMenuPosition.y}px`,
            zIndex: 10000,
          }}
          role="menu"
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              setContextMenuOpen(false);
            }
          }}
        >
          <button
            type="button"
            className="px-3 py-2 hover:bg-gray-700 cursor-pointer border-b border-gray-700 text-left w-full"
            onClick={handleSetDefaultView}
          >
            <span>Set as default view</span>
          </button>
          <button
            type="button"
            className="relative px-3 py-2 hover:bg-gray-700 cursor-pointer flex items-center justify-between text-left w-full"
            onMouseEnter={() => setShowPanoramaSubmenu(true)}
            onMouseLeave={() => setShowPanoramaSubmenu(false)}
            aria-haspopup="menu"
            aria-expanded={showPanoramaSubmenu}
          >
            <span>Link to panorama</span>
            <ChevronRight className="h-4 w-4" />
            {showPanoramaSubmenu && (
              <div
                className="absolute left-full top-0 ml-1 bg-gray-800 border border-gray-700 rounded-md shadow-lg text-white min-w-[200px] max-h-96 overflow-y-auto z-50"
                role="menu"
                onMouseEnter={() => setShowPanoramaSubmenu(true)}
                onMouseLeave={() => setShowPanoramaSubmenu(false)}
              >
                {panoramas.length === 0 ? (
                  <div className="px-3 py-2 text-gray-400">No panoramas available</div>
                ) : (
                  panoramas.map((panorama) => (
                    <button
                      key={panorama.id}
                      type="button"
                      className="px-3 py-2 hover:bg-gray-700 cursor-pointer text-left w-full"
                      onClick={() => handleLinkPanorama(panorama.id)}
                    >
                      {panorama.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </button>
          <button
            type="button"
            className="px-3 py-2 hover:bg-gray-700 cursor-pointer border-t border-gray-700 text-left w-full"
            onClick={handleAddManualHotspot}
          >
            <span>Add manual hotspot</span>
          </button>
        </div>
      )}
      {markerContextMenuOpen && markerContextMenuPosition && (
        <div
          className="fixed bg-gray-800 border border-gray-700 rounded-md shadow-lg text-white min-w-[200px]"
          style={{
            left: `${markerContextMenuPosition.x}px`,
            top: `${markerContextMenuPosition.y}px`,
            zIndex: 10000,
          }}
          role="menu"
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              setMarkerContextMenuOpen(false);
            }
          }}
        >
          <button
            type="button"
            className="px-3 py-2 hover:bg-gray-700 cursor-pointer text-red-400 text-left w-full"
            onClick={handleDeletePanoramaFromMap}
          >
            <span>Delete from map</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default Maps;
